import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RewardiouLogo from './RewardiouLogo';
import { useAppContext } from '../contexts/AppContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Login, Logout, PersonAdd } from '@mui/icons-material';
import { Link as ScrollLink } from 'react-scroll';
import { useNavigate } from 'react-router-dom';
import { Select, MenuItem as MuiMenuItem, ListItemText, Typography } from '@mui/material';

// @ts-ignore
import Flag from 'react-world-flags';
import createAuthService from '../utils/AuthService';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  backgroundColor: alpha(theme.palette.background.default, 0.72),
  boxShadow: theme.shadows[1],
  padding: '8px 12px',
}));

interface AppAppBarProps {
  hideNavigation?: boolean;
  hideAuthButtons?: boolean;
  hideNavigationOnly?: boolean;
  marketingMode?: boolean;
}

export default function AppAppBar({
  hideNavigation = false,
  hideAuthButtons = false,
  hideNavigationOnly = false,
  marketingMode = false,
}: AppAppBarProps) {
  const [open, setOpen] = React.useState(false);
  const { appState, updateAppState } = useAppContext();
  const { language, setLanguage, translations } = useLanguage();
  const navigate = useNavigate();
  const authService = createAuthService();

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const navigationItems = React.useMemo(
    () => [
      { label: translations.features, to: 'features' },
      { label: translations.highlights, to: 'highlights' },
      { label: translations.pricing, to: 'pricing' },
      { label: translations.contact_us, to: 'contact' },
    ],
    [translations],
  );
  const languageOptions = React.useMemo(
    () => [
      { value: 'en', label: 'English', flag: 'GB' },
      { value: 'pt', label: 'Português', flag: 'PT' },
      { value: 'de', label: 'Deutsch', flag: 'DE' },
      { value: 'tr', label: 'Türkçe', flag: 'TR' },
    ],
    [],
  );

  const handleLogOut = () => {
    updateAppState({
      isAuthenticated: false,
      accessToken: '',
      refreshToken: '',
      user: null,
    });
    navigate('/');
  };

  const handleLanguageChange = async (languagecode: string) => {
    setLanguage(languagecode);
    await authService.updateLanguageAsync(languagecode);
  };

  const showMarketingNavigation = marketingMode && !hideNavigation && !hideNavigationOnly;

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        top: 0,
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: {
          xs: marketingMode ? 'calc(var(--template-frame-height, 0px) + 14px)' : 'calc(var(--template-frame-height, 0px) + 16px)',
          md: 'calc(var(--template-frame-height, 0px) + 28px)',
        },
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar
          variant="dense"
          disableGutters
          sx={(theme) => ({
            borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
            px: { xs: 1.25, md: 1.5 },
            py: { xs: 0.75, md: 1 },
            backgroundColor: marketingMode ? alpha(theme.palette.background.paper, 0.92) : undefined,
          })}
        >
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
            <RewardiouLogo />
            {showMarketingNavigation && (
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
                {navigationItems.map((item) => (
                  <ScrollLink key={item.to} to={item.to} smooth duration={500} offset={-88}>
                    <Button variant="text" color="info" size="small">
                      {item.label}
                    </Button>
                  </ScrollLink>
                ))}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Select
              value={language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              size="small"
              sx={{ minWidth: 120, bgcolor: 'background.paper', color: 'text.primary', borderRadius: 2 }}
            >
              {languageOptions.map((option) => (
                <MuiMenuItem key={option.value} value={option.value}>
                  <Flag code={option.flag} style={{ width: 25, height: 15, marginRight: 8 }} />
                  <ListItemText primary={option.label} />
                </MuiMenuItem>
              ))}
            </Select>

            {!hideAuthButtons &&
              (!appState.isAuthenticated ? (
                <>
                  <Button onClick={() => navigate('/signin')} color="primary" variant="outlined" size="small" endIcon={<Login />}>
                    {translations.signin}
                  </Button>
                  <Button onClick={() => navigate('/signup')} color="primary" variant="outlined" size="small" endIcon={<PersonAdd />}>
                    {translations.signup}
                  </Button>
                </>
              ) : (
                <Button onClick={handleLogOut} color="primary" variant="outlined" size="small" endIcon={<Logout />}>
                  {translations.logout}
                </Button>
              ))}
          </Box>

          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, alignItems: 'center' }}>
            {marketingMode ? (
              <>
                <Select
                  value={language}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  size="small"
                  renderValue={(value) => String(value).toUpperCase()}
                  sx={{
                    minWidth: 74,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    borderRadius: 2,
                    '& .MuiSelect-select': {
                      py: 0.75,
                    },
                  }}
                >
                  {languageOptions.map((option) => (
                    <MuiMenuItem key={option.value} value={option.value}>
                      <Flag code={option.flag} style={{ width: 25, height: 15, marginRight: 8 }} />
                      <ListItemText primary={option.label} />
                    </MuiMenuItem>
                  ))}
                </Select>
              </>
            ) : (
              <>
                <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
                  <MenuIcon />
                </IconButton>
                <Drawer
                  anchor="top"
                  open={open}
                  onClose={toggleDrawer(false)}
                  PaperProps={{
                    sx: {
                      top: 'var(--template-frame-height, 0px)',
                    },
                  }}
                >
                  <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton onClick={toggleDrawer(false)}>
                        <CloseRoundedIcon />
                      </IconButton>
                    </Box>

                    {showMarketingNavigation && (
                      <>
                        {navigationItems.map((item) => (
                          <ScrollLink key={item.to} to={item.to} smooth duration={500} offset={-88}>
                            <MenuItem onClick={toggleDrawer(false)}>{item.label}</MenuItem>
                          </ScrollLink>
                        ))}
                        <Divider sx={{ my: 3 }} />
                      </>
                    )}

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Language
                      </Typography>
                      <Select
                        value={language}
                        onChange={(e) => handleLanguageChange(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ bgcolor: 'background.paper', color: 'text.primary', borderRadius: 2 }}
                      >
                        {languageOptions.map((option) => (
                          <MuiMenuItem key={option.value} value={option.value}>
                            <Flag code={option.flag} style={{ width: 25, height: 15, marginRight: 8 }} />
                            <ListItemText primary={option.label} />
                          </MuiMenuItem>
                        ))}
                      </Select>
                    </Box>

                    {!hideAuthButtons && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {!appState.isAuthenticated ? (
                          <>
                            <Button
                              onClick={() => {
                                navigate('/signin');
                                toggleDrawer(false)();
                              }}
                              color="primary"
                              variant="outlined"
                              fullWidth
                              endIcon={<Login />}
                            >
                              {translations.signin}
                            </Button>
                            <Button
                              onClick={() => {
                                navigate('/signup');
                                toggleDrawer(false)();
                              }}
                              color="primary"
                              variant="outlined"
                              fullWidth
                              endIcon={<PersonAdd />}
                            >
                              {translations.signup}
                            </Button>
                          </>
                        ) : (
                          <Button
                            onClick={() => {
                              handleLogOut();
                              toggleDrawer(false)();
                            }}
                            color="primary"
                            variant="outlined"
                            fullWidth
                            endIcon={<Logout />}
                          >
                            {translations.logout}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                </Drawer>
              </>
            )}
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}
